import type { MortgageData } from '@/components/calculator/CalculatorTabs';
import type { RiskAssessmentData, CashFlowAnalysis, RiskFactorWeights } from '@/types/riskAssessment';
import { calculateEffectiveSellerCredit } from '@/components/calculator/utils/transactionSafeCalculations';

export class EnhancedRiskAssessmentService {
  private readonly riskWeights: RiskFactorWeights = {
    loanToValue: 0.3,
    cashToCloseRatio: 0.25,
    sellerCreditDependency: 0.2,
    postClosingLiquidity: 0.15,
    debtToIncomeRatio: 0.1
  };

  calculateRiskAssessment(mortgageData: MortgageData, annualIncome: number = 100000): RiskAssessmentData {
    const loanAmount = mortgageData.homePrice - mortgageData.downPayment;
    const loanToValue = (loanAmount / mortgageData.homePrice) * 100;
    
    // Calculate effective seller credit
    const requestedSellerCreditAmount = (mortgageData.sellerCredit / 100) * mortgageData.homePrice;
    const effectiveSellerCreditAmount = calculateEffectiveSellerCredit(
      requestedSellerCreditAmount, 
      mortgageData.closingCosts.total
    );
    
    // Cash to close after seller credits
    const cashToClose = mortgageData.downPayment + mortgageData.closingCosts.total;
    const adjustedCashToClose = cashToClose - effectiveSellerCreditAmount;
    
    // Calculate risk factors
    const factors = {
      loanToValue,
      cashToCloseRatio: (adjustedCashToClose / annualIncome) * 100,
      sellerCreditDependency: effectiveSellerCreditAmount > 0 ? (effectiveSellerCreditAmount / cashToClose) * 100 : 0,
      postClosingLiquidity: Math.max(0, 100 - ((adjustedCashToClose / annualIncome) * 100)),
      debtToIncomeRatio: (mortgageData.monthlyPayment.total / (annualIncome / 12)) * 100
    };
    
    // Calculate weighted risk score (0-100, lower is better)
    const riskScore = this.calculateWeightedRiskScore(factors);
    const riskLevel = this.determineRiskLevel(riskScore);
    const recommendations = this.generateRecommendations(factors, effectiveSellerCreditAmount);
    
    return {
      id: `risk_${Date.now()}`,
      timestamp: new Date().toISOString(),
      homePrice: mortgageData.homePrice,
      cashToClose,
      sellerCreditAmount: effectiveSellerCreditAmount,
      sellerCreditPercentage: mortgageData.sellerCredit,
      adjustedCashToClose,
      riskScore,
      riskLevel,
      factors,
      reportGenerated: false,
      triggerEvent: 'manual',
      recommendations
    };
  }

  calculateCashFlowAnalysis(mortgageData: MortgageData): CashFlowAnalysis {
    const requestedSellerCreditAmount = (mortgageData.sellerCredit / 100) * mortgageData.homePrice;
    const effectiveSellerCreditAmount = calculateEffectiveSellerCredit(
      requestedSellerCreditAmount, 
      mortgageData.closingCosts.total
    );
    
    const cashToClose = mortgageData.downPayment + mortgageData.closingCosts.total;
    const adjustedCashToClose = cashToClose - effectiveSellerCreditAmount;
    const savingsFromCredit = effectiveSellerCreditAmount;
    
    // Determine effectiveness rating
    let effectivenessRating: CashFlowAnalysis['effectivenessRating'] = 'Poor';
    const savingsPercentage = (savingsFromCredit / cashToClose) * 100;
    
    if (savingsPercentage >= 5) effectivenessRating = 'Excellent';
    else if (savingsPercentage >= 3) effectivenessRating = 'Good';
    else if (savingsPercentage >= 1) effectivenessRating = 'Fair';
    
    return {
      cashToClose,
      adjustedCashToClose,
      sellerCreditAmount: effectiveSellerCreditAmount,
      sellerCreditPercentage: mortgageData.sellerCredit,
      savingsFromCredit,
      effectivenessRating
    };
  }

  private calculateWeightedRiskScore(factors: RiskAssessmentData['factors']): number {
    const scores = {
      loanToValue: this.getLoanToValueScore(factors.loanToValue),
      cashToCloseRatio: this.getCashToCloseRatioScore(factors.cashToCloseRatio),
      sellerCreditDependency: this.getSellerCreditDependencyScore(factors.sellerCreditDependency),
      postClosingLiquidity: this.getPostClosingLiquidityScore(factors.postClosingLiquidity),
      debtToIncomeRatio: this.getDebtToIncomeScore(factors.debtToIncomeRatio)
    };
    
    return Math.round(
      scores.loanToValue * this.riskWeights.loanToValue +
      scores.cashToCloseRatio * this.riskWeights.cashToCloseRatio +
      scores.sellerCreditDependency * this.riskWeights.sellerCreditDependency +
      scores.postClosingLiquidity * this.riskWeights.postClosingLiquidity +
      scores.debtToIncomeRatio * this.riskWeights.debtToIncomeRatio
    );
  }

  private getLoanToValueScore(ltv: number): number {
    if (ltv <= 80) return 10;
    if (ltv <= 90) return 25;
    if (ltv <= 95) return 50;
    return 80;
  }

  private getCashToCloseRatioScore(ratio: number): number {
    if (ratio <= 15) return 10;
    if (ratio <= 25) return 25;
    if (ratio <= 40) return 50;
    return 80;
  }

  private getSellerCreditDependencyScore(dependency: number): number {
    if (dependency === 0) return 10;
    if (dependency <= 20) return 20;
    if (dependency <= 40) return 40;
    return 70;
  }

  private getPostClosingLiquidityScore(liquidity: number): number {
    if (liquidity >= 80) return 10;
    if (liquidity >= 60) return 25;
    if (liquidity >= 40) return 50;
    return 80;
  }

  private getDebtToIncomeScore(dti: number): number {
    if (dti <= 28) return 10;
    if (dti <= 36) return 25;
    if (dti <= 45) return 50;
    return 80;
  }

  private determineRiskLevel(riskScore: number): RiskAssessmentData['riskLevel'] {
    if (riskScore <= 25) return 'Low';
    if (riskScore <= 45) return 'Medium';
    if (riskScore <= 65) return 'High';
    return 'Critical';
  }

  private generateRecommendations(factors: RiskAssessmentData['factors'], sellerCredit: number): string[] {
    const recommendations: string[] = [];
    
    if (factors.loanToValue > 80) {
      recommendations.push('Consider increasing down payment to reduce loan-to-value ratio');
    }
    
    if (factors.cashToCloseRatio > 30) {
      recommendations.push('High cash-to-close ratio - consider negotiating higher seller credits');
    }
    
    if (factors.sellerCreditDependency > 40) {
      recommendations.push('Heavy reliance on seller credits - ensure backup funding sources');
    }
    
    if (factors.postClosingLiquidity < 50) {
      recommendations.push('Low post-closing liquidity - maintain additional emergency funds');
    }
    
    if (factors.debtToIncomeRatio > 36) {
      recommendations.push('Consider reducing monthly obligations or increasing income');
    }
    
    if (sellerCredit > 0) {
      recommendations.push('Seller credits applied effectively - monitor closing cost estimates');
    }
    
    return recommendations;
  }
}

export const enhancedRiskAssessmentService = new EnhancedRiskAssessmentService();